import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Checkbox,
    FormControlLabel,
    Button,
    useTheme,
    Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCreateSchool } from '../../hooks/schools/useCreateSchool';
import { useUpdateSchool } from '../../hooks/schools/useUpdateSchool';
import { useUploadMedia } from '../../hooks/media/useUploadMedia';
import FormInput from '../inputs/FormInput';

const FormAddEditSchool = ({ currentData, isEdit = false }) => {
    const { i18n } = useTranslation();
    const theme = useTheme();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const isDark = theme.palette.mode === 'dark';
    const { uploadMedia, isPending: isUploading } = useUploadMedia();

    const { createSchool, isPending: isCreating } = useCreateSchool();
    const { editSchool, isUpdate } = useUpdateSchool();
    const mediaId = currentData?.image?.id ?? null;

    const loadingSubmit = isCreating || isUpdate;
    const [imageError, setImageError] = useState('');
    const [imageUploadedName, setImageUploadedName] = useState('');

    const [form, setForm] = useState({
        code: '',
        name: '',
        image: '',
        image_preview: '',
        address: '',
        phone: '',
        mobile: '',
        email: '',
        notes: '',
        is_active: true,
    });

    useEffect(() => {
        if (!isEdit || !currentData) return;

        const legacyLogoPreview =
            typeof currentData.logo === 'string' ? currentData.logo : '';

        setForm({
            code: currentData.code || '',
            name: currentData.name || '',
            image: currentData.image?.file_name || '',
            image_preview:
                currentData.image?.url ||
                currentData.image?.preview ||
                legacyLogoPreview ||
                '',
            address: currentData.address || '',
            phone: currentData.phone || '',
            mobile: currentData.mobile || '',
            email: currentData.email || '',
            notes: currentData.notes || '',
            is_active: currentData.is_active ?? true,
        });

        setImageUploadedName(currentData.image?.file_name || '');
        setImageError('');
    }, [isEdit, currentData]);

    const handleImageChange = (event) => {
        const file = event.target.files?.[0];
        setImageError('');
        setImageUploadedName('');

        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        uploadMedia(formData, {
            onSuccess: (res) => {
                const fileName = res?.data?.file_name || res?.data?.name || '';
                if (!fileName) {
                    setImageError(
                        isArabic
                            ? 'تعذر الحصول على اسم الملف من الخادم'
                            : 'Cannot get uploaded file name from server'
                    );
                    return;
                }

                setImageUploadedName(fileName);
                setForm((prev) => ({ ...prev, image: fileName }));
            },
            onError: () => {
                setImageError(
                    isArabic
                        ? 'فشل رفع الصورة، الرجاء المحاولة مرة أخرى'
                        : 'Failed to upload image, please try again'
                );
            },
        });
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setForm((prev) => ({ ...prev, [name]: checked }));
            return;
        }
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {
            code: form.code || null,
            name: form.name,
            address: form.address || null,
            phone: form.phone || null,
            mobile: form.mobile || null,
            email: form.email || null,
            notes: form.notes || null,
            is_active: !!form.is_active,
            ...(form.image ? { image: form.image } : {}),
        };

        if (isEdit && currentData?.id) {
            editSchool({ req: payload, id: currentData.id });
        } else {
            createSchool(payload);
        }
    };

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? 'rtl' : 'ltr',
                backgroundColor: isDark && 'transparent',
            }}
        >
            <form onSubmit={handleSubmit}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 3,
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <FormInput
                        required={false}
                        label={isArabic ? 'شعار المدرسة' : 'School Logo'}
                        type="file"
                        name="image"
                        value={form.image_preview}
                        onChange={handleImageChange}
                        labelImage={isArabic ? 'اختر شعار المدرسة' : 'Choose school logo'}
                        labelBioImage={
                            isArabic
                                ? 'اسحب وأسقط الصورة هنا أو انقر للاختيار'
                                : 'Drag & drop or click to upload'
                        }
                        width={150}
                        borderRadius="14px"
                        isUploading={isUploading}
                        errorMessage={imageError}
                        successMessage={
                            imageUploadedName && !imageError
                                ? isArabic
                                    ? 'تم رفع الصورة بنجاح'
                                    : 'Image uploaded successfully'
                                : ''
                        }
                        sx={{ mx: 'auto' }}
                        mediaId={mediaId}
                        onDeleted={() => {
                            setForm((prev) => ({
                                ...prev,
                                image: '',
                                image_preview: '',
                            }));
                            setImageUploadedName('');
                            setImageError('');
                        }}
                    />
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'كود المدرسة' : 'School Code'}
                            placeholder={isArabic ? 'ادخل كود المدرسة' : 'Enter school code'}
                            type="text"
                            name="code"
                            value={form.code}
                            onChange={handleChange}
                            required={false}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'اسم المدرسة' : 'School Name'}
                            placeholder={isArabic ? 'ادخل اسم المدرسة' : 'Enter school name'}
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'البريد الإلكتروني' : 'Email'}
                            placeholder="school@example.com"
                            type="text"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            required={false}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'الهاتف' : 'Phone'}
                            placeholder={isArabic ? 'رقم الهاتف' : 'Phone number'}
                            type="text"
                            name="phone"
                            value={form.phone}
                            onChange={handleChange}
                            required={false}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'الجوال' : 'Mobile'}
                            placeholder={isArabic ? 'رقم الجوال' : 'Mobile number'}
                            type="text"
                            name="mobile"
                            value={form.mobile}
                            onChange={handleChange}
                            required={false}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'العنوان' : 'Address'}
                            placeholder={isArabic ? 'عنوان المدرسة' : 'School address'}
                            type="text"
                            name="address"
                            value={form.address}
                            onChange={handleChange}
                            required={false}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormInput
                            label={isArabic ? 'ملاحظات' : 'Notes'}
                            placeholder={isArabic ? 'ملاحظات إضافية' : 'Additional notes'}
                            type="textarea"
                            name="notes"
                            value={form.notes}
                            onChange={handleChange}
                            required={false}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: { xs: 1, md: 1 } }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.is_active}
                                        name="is_active"
                                        onChange={handleChange}
                                        size="small"
                                    />
                                }
                                label={isArabic ? 'مفعّلة' : 'Active'}
                            />
                        </Box>
                    </Grid>
                </Grid>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loadingSubmit}
                    >
                        {loadingSubmit
                            ? isArabic ? 'جاري الحفظ...' : 'Saving...'
                            : isEdit
                                ? isArabic ? 'تحديث' : 'Update'
                                : isArabic ? 'حفظ' : 'Save'}
                    </Button>
                </Box>
            </form>
        </Paper>
    );
};

export default FormAddEditSchool;
